import type { UseFormReturn } from 'react-hook-form';

import { Controller } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { Button, Dialog, ExternalLink, Infotip } from '~/components';
import { Checkbox, Field } from '~/components/Form';
import { useDialog } from '~/hooks/useDialog';

import type { ContactFormType } from '../types';

import { ContactFormSection } from './ContactFormSection';
import { checkboxRowStyle } from './contactFormSections.css';

type ContactCustomAgreementFormType = Pick<ContactFormType, 'customAgreement'>;

export const ContactCustomAgreementFormSection = <T extends ContactCustomAgreementFormType>({
  form,
}: {
  form: T extends ContactCustomAgreementFormType ? UseFormReturn<T> : never;
}) => {
  const { control, formState } = form;

  const confirmationDialog = useDialog<void, boolean>();

  const { t } = useTranslation(['clients', 'common', 'validation']);

  return (
    <ContactFormSection
      description={<Trans components={{ a: <ExternalLink /> }} i18nKey="clients:createEdit.subsections.customAgreement.description" />}
      title={t('clients:createEdit.subsections.customAgreement.title')}
    >
      <Controller
        control={control}
        name="customAgreement"
        render={({ field: { name, onBlur, onChange, ref, value } }) => (
          <Field.Root name={name}>
            <div className={checkboxRowStyle}>
              <Checkbox.Root
                checked={value}
                // An agreement that's already active can't be revoked here.
                disabled={!!formState.defaultValues?.customAgreement}
                onBlur={onBlur}
                onCheckedChange={async (checked) => {
                  if (!checked) return onChange(checked);

                  if (await confirmationDialog.open()) onChange(checked);
                }}
                ref={ref}
              >
                <Checkbox.Label>{t('clients:fields.customAgreement.label')}</Checkbox.Label>
              </Checkbox.Root>

              <Infotip>{t('clients:fields.customAgreement.tooltip')}</Infotip>
            </div>
          </Field.Root>
        )}
      />

      {/* Confirmation dialog */}
      <Dialog.Root onOpenChange={() => confirmationDialog.close(false)} open={confirmationDialog.isOpen}>
        <Dialog.Body>
          <Dialog.Header title={t('clients:customAgreementDialog.title')} />

          <p>
            <Trans components={{ a: <ExternalLink /> }} i18nKey="clients:customAgreementDialog.description" />
          </p>

          <Dialog.Actions>
            <Button emphasis="secondary" onClick={() => confirmationDialog.close(false)}>
              {t('clients:customAgreementDialog.cancel')}
            </Button>

            <Button onClick={() => confirmationDialog.close(true)}>{t('clients:customAgreementDialog.submit')}</Button>
          </Dialog.Actions>
        </Dialog.Body>
      </Dialog.Root>
    </ContactFormSection>
  );
};
